import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { authService } from '@/services';
import { useI18n } from '@/i18n/use-i18n';
import '@/styles/auth-page.css';

type SignupFormValues = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export function Signup() {
  const { strings } = useI18n();
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const highlights = strings.signup.highlights;
  const signupSchema = useMemo(
    () =>
      yup
        .object({
          fullName: yup.string().required(strings.signup.validations.fullNameRequired),
          email: yup
            .string()
            .required(strings.signup.validations.emailRequired)
            .email(strings.signup.validations.emailInvalid),
          password: yup
            .string()
            .required(strings.signup.validations.passwordRequired)
            .min(8, strings.signup.validations.passwordMin),
          confirmPassword: yup
            .string()
            .required(strings.signup.validations.confirmPasswordRequired)
            .oneOf([yup.ref('password')], strings.signup.validations.confirmPasswordMatch),
        })
        .required(),
    [strings],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormValues>({
    resolver: yupResolver(signupSchema),
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const isFormDisabled = isSubmitting;

  const onSubmit = async (values: SignupFormValues) => {
    setGeneralError('');
    setSuccessMessage('');

    const { error } = await authService.signUp({
      email: values.email,
      password: values.password,
      fullName: values.fullName,
      redirectTo: `${window.location.origin}/`,
    });

    if (error) {
      setGeneralError(error.message);
      return;
    }

    setSuccessMessage(strings.signup.successMessage);
    reset();
  };

  return (
    <main className="auth-page">
      <section className="auth-page__hero">
        <div className="auth-page__hero-content">
          <p className="auth-page__badge">Touzi</p>
          <h1>
            {strings.signup.heroTitle} <span>{strings.signup.heroEmphasis}</span>
          </h1>
          <p className="auth-page__subtitle">
            {strings.signup.subtitle}
          </p>
          <ul className="auth-page__highlights">
            {highlights.map((item) => (
              <li key={item.title}>
                <strong>{item.title}</strong>
                <span>{item.description}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="auth-page__panel" aria-label={strings.signup.panelAriaLabel}>
        <div className="auth-page__card">
          <header>
            <h2>{strings.signup.formTitle}</h2>
            <p>{strings.signup.formSubtitle}</p>
          </header>

          <form className="auth-page__form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <label htmlFor="fullName">{strings.signup.fullNameLabel}</label>
            <input
              id="fullName"
              type="text"
              autoComplete="name"
              placeholder={strings.signup.fullNamePlaceholder}
              {...register('fullName')}
              aria-invalid={errors.fullName ? 'true' : 'false'}
              disabled={isFormDisabled}
            />
            {errors.fullName && (
              <span className="auth-page__field-error" role="alert">
                {errors.fullName.message}
              </span>
            )}

            <label htmlFor="signup-email">{strings.signup.emailLabel}</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              placeholder={strings.signup.emailPlaceholder}
              {...register('email')}
              aria-invalid={errors.email ? 'true' : 'false'}
              disabled={isFormDisabled}
            />
            {errors.email && (
              <span className="auth-page__field-error" role="alert">
                {errors.email.message}
              </span>
            )}

            <label htmlFor="signup-password">{strings.signup.passwordLabel}</label>
            <input
              id="signup-password"
              type="password"
              autoComplete="new-password"
              placeholder={strings.signup.passwordPlaceholder}
              {...register('password')}
              aria-invalid={errors.password ? 'true' : 'false'}
              disabled={isFormDisabled}
            />
            {errors.password && (
              <span className="auth-page__field-error" role="alert">
                {errors.password.message}
              </span>
            )}

            <label htmlFor="signup-confirm-password">
              {strings.signup.confirmPasswordLabel}
            </label>
            <input
              id="signup-confirm-password"
              type="password"
              autoComplete="new-password"
              placeholder={strings.signup.confirmPasswordPlaceholder}
              {...register('confirmPassword')}
              aria-invalid={errors.confirmPassword ? 'true' : 'false'}
              disabled={isFormDisabled}
            />
            {errors.confirmPassword && (
              <span className="auth-page__field-error" role="alert">
                {errors.confirmPassword.message}
              </span>
            )}

            {generalError && (
              <div
                className="auth-page__general-feedback auth-page__general-feedback--error"
                role="alert"
                aria-live="assertive"
              >
                {generalError}
              </div>
            )}

            {successMessage && (
              <div
                className="auth-page__general-feedback auth-page__general-feedback--success"
                role="status"
                aria-live="polite"
              >
                {successMessage}
              </div>
            )}

            <button type="submit" className="auth-page__submit" disabled={isFormDisabled}>
              {isSubmitting ? strings.signup.submitLoading : strings.signup.submitLabel}
            </button>
          </form>

          <p className="auth-page__switch">
            {strings.signup.switchPrompt} <Link to="/">{strings.signup.switchAction}</Link>
          </p>
        </div>
      </section>
    </main>
  );
}
